import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./NumberGenerator.module.scss";
import { getSeededRandomNumber } from "../../utils/randomUtils";

const NumberGenerator = () => {
  const navigate = useNavigate();
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [digitCount, setDigitCount] = useState(3);
  const [history, setHistory] = useState([]);
  const digitRefs = useRef([]);

  useEffect(() => {
    const count = max.toString().length;
    setDigitCount(count);
    digitRefs.current = digitRefs.current.slice(0, count);
  }, [max]);

  // Formátování výsledku na požadovaný počet číslic (s doplněním nul)
  const formatResult = (num) => num.toString().padStart(digitCount, "0");

  // Zachytávání kláves pro omezení vstupu (blokujeme -, +, e)
  const handleKeyDown = (e) => {
    if (e.key === "-" || e.key === "+" || e.key.toLowerCase() === "e") {
      e.preventDefault();
    }
  };

  // Kontrola a nastavení minimální hodnoty
  const handleChangeMin = (e) => {
    const value = e.target.value;
    // Pokud je záporná, zobrazíme "Neplatný rozsah" a vynulujeme vstup
    if (parseInt(value, 10) < 0) {
      setResult("Neplatný rozsah");
      setMin("");
    } else {
      setMin(value);
    }
  };

  // Kontrola a nastavení maximální hodnoty
  const handleChangeMax = (e) => {
    const value = e.target.value;
    if (parseInt(value, 10) < 0) {
      setResult("Neplatný rozsah");
      setMax("");
    } else {
      setMax(value);
    }
  };

  const handleGenerate = async () => {
    const minNum = parseInt(min, 10);
    const maxNum = parseInt(max, 10);

    // Kontrola validního rozsahu
    if (
      isNaN(minNum) ||
      isNaN(maxNum) ||
      minNum < 0 ||
      maxNum < 0 ||
      minNum > maxNum
    ) {
      setResult("Neplatný rozsah");
      return;
    }

    // Výpočet náhodného čísla
    const range = maxNum - minNum + 1;
    const randomNumber = await getSeededRandomNumber(range);
    const finalResult = minNum + randomNumber;

    // Roztočení "slotu" a zobrazení výsledku
    setSpinning(true);
    setResult(finalResult);
    const formatted = formatResult(finalResult);

    // Pokud jde o 3 číslice, chceme pořadí zastavení: index 2, pak 1, pak 0.
    // Pro jiný počet číslic použijeme standardní obrácené pořadí.
    let order = [];
    if (digitCount === 3) {
      order = [2, 1, 0];
    } else {
      order = [...Array(digitCount).keys()].reverse();
    }

    order.forEach((digitIndex, i) => {
      setTimeout(() => {
        if (digitRefs.current[digitIndex]) {
          const digit = parseInt(formatted[digitIndex], 10);
          digitRefs.current[digitIndex].classList.remove(styles.spinning);
          digitRefs.current[digitIndex].classList.add(styles.stopped);
          digitRefs.current[digitIndex].style.transform = `translateY(-${
            digit * 40
          }px) translateX(-50%)`;
        }
        // Po poslední číslici ukládáme historii a ukončíme "točení"
        if (i === order.length - 1) {
          setSpinning(false);
          setHistory((prev) => [finalResult, ...prev].slice(0, 10));
        }
      }, i * 500);
    });
  };

  // Zobrazení jednotlivých číslic
  const renderDigits = () =>
    result === "Neplatný rozsah"
      ? result
      : (result !== null
          ? formatResult(result)
          : new Array(digitCount).fill("?").join("")
        )
          .split("")
          .map((digit, index) => (
            <div key={index} className={styles["digit-container"]}>
              <div
                className={`${styles.digit} ${
                  spinning ? styles.spinning : styles.stopped
                }`}
                ref={(el) => (digitRefs.current[index] = el)}
              >
                {[...Array(10)].map((_, i) => (
                  <div key={i} style={{ height: "40px", lineHeight: "40px" }}>
                    {i}
                  </div>
                ))}
              </div>
            </div>
          ));

  return (
    <div className={styles.container}>
      <div className={styles.menu}>
        <button className={styles.backButton} onClick={() => navigate("/")}>
          Zpět
        </button>
        <h3>Generátor čísel</h3>
      </div>
      <div className={styles.content}>
        <div className={styles.controls}>
          <input
            type="number"
            placeholder="Min"
            value={min}
            onKeyDown={handleKeyDown}   // Blokování kláves
            onChange={handleChangeMin}  // Kontrola záporné hodnoty
            className={styles.input}
          />
          <input
            type="number"
            placeholder="Max"
            value={max}
            onKeyDown={handleKeyDown}
            onChange={handleChangeMax}
            className={styles.input}
          />
          <button
            onClick={handleGenerate}
            className={styles.generateButton}
            disabled={spinning}
          >
            {spinning ? "Generuji..." : "Generovat číslo"}
          </button>
        </div>
        <div className={styles.resultDisplay}>
       <strong>{renderDigits()}</strong>
        </div>
        {history.length > 0 && (
          <div className={styles.history}>
            <h4>Historie</h4>
            <ul>
              {history.map((num, i) => (
                <li key={i}>
                  #{i + 1}: {num}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberGenerator;
